import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ThemeProvider } from './context/ThemeContext';
import SuspendedModal from './components/common/SuspendedModal';
import AppToaster from './components/common/AppToaster';
import {
  Register,
  Login,
  VerifyOTP,
  ForgotPassword,
  ResetPassword,
} from './components/auth';
import RequireRole from './components/auth/RequireRole';
import RequireMerchant from './components/auth/RequireMerchant';
import { DashboardLayout } from './components/layout';
import OnboardingLayout from './components/onboarding/OnboardingLayout';
import OnboardingResume from './components/onboarding/OnboardingResume';
import Preparing from './components/onboarding/Preparing';
import BusinessProfile from './components/onboarding/steps/BusinessProfile';
import UseCases from './components/onboarding/steps/UseCases';
import Activate from './components/onboarding/steps/Activate';
import TestPayment from './components/onboarding/steps/TestPayment';
import Success from './components/onboarding/steps/Success';
import Plan from './components/onboarding/steps/Plan';
import {
  Home,
  Balance,
  PaymentLinks,
  Transactions,
  Invoices,
  InvoiceDetail,
  Settings,
  Developer,
  PaymentPage,
  PublicInvoiceView,
  Merchants,
  MerchantDetail,
  Payouts,
  PayoutDetail,
  PayoutRequests,
  Guide,
  WalletChangeRequests,
  Networks,
  Tokens,
  AutoConversion,
  Analytics,
  PayoutReview,
  Treasury,
  Solvency,
  AdminLogs,
  Reports,
  AddressBook,
  RulesEngine,
  NotFound,
} from './pages';
import Billing from './pages/Billing';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

function App() {
  return (
    <ThemeProvider>
      <AppToaster />
      <SuspendedModal />
      <Router>
        <Routes>
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route path="/verify-otp" element={<VerifyOTP />} />

          <Route path="/pay/link/:linkId" element={<PaymentPage />} />
          <Route path="/pay/invoice/:linkId" element={<PaymentPage />} />
          <Route path="/pay/session/:sessionId" element={<PaymentPage />} />
          <Route path="/invoice/:linkId" element={<PublicInvoiceView />} />

          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />

          {/* Declared outside the wizard layout on purpose: the preparing
              screen is full-bleed, with no rail, so leaving setup reads as a
              handoff rather than another step. */}
          <Route
            path="/onboarding/preparing"
            element={
              <PrivateRoute>
                <RequireMerchant>
                  <Preparing />
                </RequireMerchant>
              </PrivateRoute>
            }
          />

          {/* RequireMerchant, not RequireRole: this whole tree is merchant
              setup, and a super admin has no business profile, plan or test
              payment to configure. Every post-login redirect points at
              /onboarding regardless of role, so without this an admin signing
              in is walked through merchant onboarding. */}
          <Route
            path="/onboarding"
            element={
              <PrivateRoute>
                <RequireMerchant>
                  <OnboardingLayout />
                </RequireMerchant>
              </PrivateRoute>
            }
          >
            <Route index element={<OnboardingResume />} />
            <Route path="business" element={<BusinessProfile />} />
            <Route path="use-cases" element={<UseCases />} />
            <Route path="plan" element={<Plan />} />
            {/* No longer wizard steps (see steps.js) but still routable:
                verification gates going live, and Home's empty state links
                straight to /onboarding/payment. */}
            <Route path="activate" element={<Activate />} />
            <Route path="payment" element={<TestPayment />} />
            <Route path="done" element={<Success />} />
          </Route>

          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<Home />} />
            <Route path="balance" element={<Balance />} />
            <Route path="payment-links" element={<PaymentLinks />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/:id" element={<InvoiceDetail />} />
            <Route path="networks" element={<Networks />} />
            <Route path="tokens" element={<Tokens />} />
            <Route path="settings" element={<Settings />} />
            <Route path="billing" element={<Billing />} />
            <Route path="developer" element={<Developer />} />
            <Route path="merchants" element={<RequireRole><Merchants /></RequireRole>} />
            <Route path="merchants/:id" element={<RequireRole><MerchantDetail /></RequireRole>} />
            <Route path="payouts" element={<Payouts />} />
            <Route path="payouts/:id" element={<PayoutDetail />} />
            <Route path="auto-conversion" element={<AutoConversion />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="payout-requests" element={<RequireRole><PayoutRequests /></RequireRole>} />
            <Route path="payout-requests/:requestId/review" element={<RequireRole><PayoutReview /></RequireRole>} />
            <Route path="wallet-change-requests" element={<RequireRole><WalletChangeRequests /></RequireRole>} />
            <Route path="admin/treasury" element={<RequireRole><Treasury /></RequireRole>} />
            <Route path="admin/solvency" element={<RequireRole><Solvency /></RequireRole>} />
            <Route path="admin/logs" element={<RequireRole><AdminLogs /></RequireRole>} />
            <Route path="admin/reports" element={<RequireRole><Reports /></RequireRole>} />
            <Route path="admin/address-book" element={<RequireRole><AddressBook /></RequireRole>} />
            <Route path="admin/rules" element={<RequireRole><RulesEngine /></RequireRole>} />
            <Route path="guide" element={<Guide />} />
          </Route>

          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
